import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef } from 'react';
import { Box, IconButton, TextField, InputAdornment, Collapse, useTheme, useMediaQuery } from '@mui/material';
import { SearchNormal1, CloseCircle, SearchNormal } from 'iconsax-react';
import { between } from '@ui/style/style';
import { Icon } from '@iconify/react';
const ResponsiveSearch = ({ value, onChange, placeholder = 'Search...', onClear, breakpoint = 'lg' }) => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down(breakpoint));
    const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
    const [internalSearch, setInternalSearch] = useState(value);
    const debounceRef = useRef(null);
    // Sync internal state with external value prop
    useEffect(() => {
        setInternalSearch(value);
    }, [value]);
    const handleSearchChange = (newValue) => {
        const prevLength = internalSearch.length;
        setInternalSearch(newValue);
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(() => {
            if (newValue.length >= 3 || newValue.length === 0) {
                onChange(newValue);
            }
        }, 500);
    };
    const handleClear = () => {
        setInternalSearch('');
        onChange('');
        if (onClear) {
            onClear();
        }
        if (isSmallScreen) {
            setMobileSearchOpen(false);
        }
    };
    const handleMobileToggle = () => {
        if (!mobileSearchOpen && internalSearch) {
            // If opening and there's a value, clear it
            setInternalSearch('');
            onChange('');
            if (onClear) {
                onClear();
            }
        }
        setMobileSearchOpen(!mobileSearchOpen);
    };
    if (isSmallScreen) {
        return (_jsxs(Box, { children: [_jsx(IconButton, { onClick: handleMobileToggle, size: 'small', children: _jsx(SearchNormal1, { size: 20, variant: 'Outline' }) }), _jsx(Collapse, { in: mobileSearchOpen, children: _jsxs(Box, { sx: {
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            left: 0,
                            zIndex: 1000,
                            height: 54,
                            backdropFilter: 'blur(20px)',
                            backgroundColor: theme.palette.common.white92,
                            px: 2,
                            gap: 1,
                            ...between
                        }, children: [_jsx(TextField, { fullWidth: true, value: internalSearch, onChange: (e) => handleSearchChange(e.target.value), placeholder: placeholder, variant: 'outlined', size: 'small', autoFocus: true, hiddenLabel: true, InputProps: {
                                    startAdornment: (_jsx(InputAdornment, { position: 'start', children: _jsx(SearchNormal, { size: 24, variant: 'Outline', color: theme.palette.text.secondary }) }))
                                } }), _jsx(Icon, { color: theme.palette.text.primary, style: {
                                    cursor: 'pointer'
                                }, icon: 'iconamoon:close-bold', onClick: handleClear })] }) })] }));
    }
    // Desktop Search Field
    return (_jsx(TextField, { value: internalSearch, onChange: (e) => handleSearchChange(e.target.value), placeholder: placeholder, variant: 'outlined', hiddenLabel: true, size: 'small', sx: {
            minWidth: 250,
            '& .MuiFilledInput-root': {
                height: '48px',
                display: 'flex',
                alignItems: 'center'
            }
        }, InputProps: {
            startAdornment: (_jsx(InputAdornment, { position: 'start', children: _jsx(SearchNormal, { variant: 'Outline', size: 24, color: theme.palette.grey[700] }) })),
            endAdornment: internalSearch && (_jsx(InputAdornment, { position: 'end', children: _jsx(CloseCircle, { style: {
                        cursor: 'pointer'
                    }, size: 16, color: theme.palette.text.secondary, onClick: handleClear }) }))
        } }));
};
export default ResponsiveSearch;
